import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X, Zap, Shield, Sparkles, Lock } from 'lucide-react';
import { Button } from './button';
import { AnimatedGroup } from './animated-group';
import { TestimonialsColumn } from './testimonials-columns-1';
import { cn } from '../../lib/utils';
import { AppLogo } from '../Layout/AppLogo';
import { ThemeToggle } from '../Layout/ThemeToggle';

// PromptOS-specific testimonials data
const testimonials = [
  {
    text: "PromptOS transformed my content creation workflow. Having all my AI prompts organized and searchable has saved me hours every week.",
    image: "https://images.unsplash.com/photo-1494790108755-2616b612786?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    name: "Sarah Chen",
    role: "Content Creator",
  },
  {
    text: "The AI enhancement feature is incredible. My prompts are now more effective and produce better results across all platforms.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    name: "Marcus Rodriguez",
    role: "Digital Marketer",
  },
  {
    text: "As a developer, I love the template system. I can create reusable prompts with variables that adapt to different projects seamlessly.",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    name: "Alex Thompson",
    role: "Software Engineer",
  },
  {
    text: "The security and privacy features give me confidence. My proprietary prompts are safe, and I own my data completely.",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    name: "Jennifer Park",
    role: "AI Consultant",
  },
  {
    text: "PromptOS has become essential for my creative agency. We can collaborate on prompts and maintain consistent quality across all projects.",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    name: "David Kim",
    role: "Creative Director",
  },
  {
    text: "The categorization features are brilliant. I can find the exact prompt I need in seconds, even with hundreds in my library.",
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    name: "Emily Watson",
    role: "Social Media Manager",
  },
  {
    text: "Being able to track recently executed prompts and see what works best has improved my AI workflow dramatically.",
    image: "https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    name: "Michael Foster",
    role: "YouTuber",
  },
  {
    text: "The export functionality is perfect for backing up my prompt collection. I feel secure knowing I can always access my work.",
    image: "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    name: "Lisa Zhang",
    role: "Freelance Writer",
  },
  {
    text: "PromptOS has revolutionized how I manage prompts for my e-commerce business. The template variations save me so much time.",
    image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3",
    name: "James Wilson",
    role: "E-commerce Owner",
  },
];

const firstColumn = testimonials.slice(0, 3);
const secondColumn = testimonials.slice(3, 6);
const thirdColumn = testimonials.slice(6, 9);

export function HeroSection() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false); // Keep this state

  return (
    <div className="min-h-screen bg-gradient-to-br from-modular-shadow-quartz via-modular-graphite-smoke to-modular-frosted-indigo 
      dark:bg-dark-gradient relative overflow-hidden">
      
      {/* Subtle animated particles background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-1 h-1 bg-modular-accent-blue/40 dark:bg-modular-accent-blue/20 rounded-full animate-ember-float opacity-40 dark:opacity-20" />
        <div className="absolute top-40 right-20 w-0.5 h-0.5 bg-modular-accent-emerald/40 dark:bg-modular-accent-emerald/20 rounded-full animate-ember-float opacity-30 dark:opacity-15" 
          style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-32 left-1/4 w-1 h-1 bg-modular-accent-blue/30 dark:bg-modular-accent-blue/15 rounded-full animate-ember-float opacity-35 dark:opacity-18" 
          style={{ animationDelay: '2s' }} />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AppLogo size="md" animated={false} />
            <div className="ml-1">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                PromptOS
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium -mt-1">
                AI Prompt Operating System
              </p>
            </div>
          </div>
          
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-white/80 hover:text-white transition-colors">Features</a>
            <a href="#testimonials" className="text-white/80 hover:text-white transition-colors">Testimonials</a>
            <Link
              to="/auth" // Keep this link
              className="bg-modular-primary-gradient text-white px-6 py-2 rounded-lg hover:shadow-modular-glow 
                transition-all duration-300 font-semibold"
            >
              Get Started
            </Link>
          </div>
          
          <div className="flex items-center space-x-4 md:hidden">
            <ThemeToggle />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white p-2"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white/95 dark:bg-dark-surface/95 
            backdrop-blur-xl border-b border-modular-stone-alloy/30 dark:border-dark-border/50 p-6">
            <div className="space-y-4">
              <a href="#features" className="block text-gray-900 dark:text-dark-text hover:text-fire-600 dark:hover:text-fire-400">Features</a>
              <a href="#testimonials" className="block text-gray-900 dark:text-dark-text hover:text-fire-600 dark:hover:text-fire-400">Testimonials</a>
              <Link
                to="/auth"
                className="block bg-fire-gradient text-white px-6 py-3 rounded-lg text-center font-semibold"
              >
                Get Started
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-24">
        <div className="text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-modular-canvas-fog dark:bg-modular-shadow-quartz/20 border border-modular-stone-alloy dark:border-modular-graphite-smoke 
            rounded-full px-4 py-2 mb-8">
            <span className="text-modular-graphite-smoke dark:text-modular-ivory-mist text-sm font-medium">✨ Introducing PromptOS</span>
            <span className="block h-4 w-0.5 bg-modular-stone-alloy dark:bg-modular-stone-alloy"></span>
            <Link to="/auth" className="flex items-center gap-1 text-modular-frosted-indigo dark:text-modular-frosted-indigo text-sm font-medium hover:text-modular-desert-mauve dark:hover:text-modular-desert-mauve">
              Learn more
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-8">
            <span className="bg-gradient-to-r from-modular-accent-blue via-blue-400 to-blue-200 bg-clip-text text-transparent">
              Your AI Prompt Operating System
            </span>
          </h1>

          {/* Description */}
          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-12 leading-relaxed">
            The most advanced platform for creating, managing, and executing AI prompts. 
            Built for creators, marketers, and AI enthusiasts who demand precision and efficiency.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link
              to="/auth"
              className="bg-modular-primary-gradient text-white px-8 py-4 rounded-lg hover:shadow-modular-glow
                transition-all duration-300 font-semibold text-lg flex items-center"
            >
              <Lock className="h-5 w-5 mr-2" />
              Get Started Free
            </Link>
            <a
              href="#features"
              className="text-white border border-white/30 hover:border-white/50 px-8 py-4 rounded-lg 
                transition-all duration-300 font-semibold text-lg flex items-center"
            >
              <Sparkles className="h-5 w-5 mr-2" />
              Explore Features
            </a>
          </div>

          {/* Preview Image */}
          <div className="relative">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-4 shadow-2xl mx-auto max-w-4xl"> {/* Keep white/10 */}
              <img
                src="https://images.unsplash.com/photo-1551650975-87deedd944c3?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3"
                alt="PromptOS Dashboard"
                className="w-full h-auto rounded-xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="bg-white dark:bg-dark-bg py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why PromptOS?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Built for creators, entrepreneurs, and AI power users who demand the best.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-xl bg-modular-canvas-fog dark:bg-dark-surface border border-modular-stone-alloy dark:border-dark-border">
              <div className="w-12 h-12 bg-modular-primary-gradient rounded-full flex items-center justify-center mx-auto mb-4"> {/* Keep primary gradient */}
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Secure & Private
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Your prompts are encrypted and stored securely. Full ownership and privacy guaranteed.
              </p>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-modular-canvas-fog dark:bg-dark-surface border border-modular-stone-alloy dark:border-dark-border">
              <div className="w-12 h-12 bg-modular-secondary-gradient rounded-full flex items-center justify-center mx-auto mb-4"> {/* Keep secondary gradient */}
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                AI-Powered
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Enhance prompts, generate variations, and get intelligent suggestions powered by AI.
              </p>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-modular-canvas-fog dark:bg-dark-surface border border-modular-stone-alloy dark:border-dark-border">
              <div className="w-12 h-12 bg-modular-warm-gradient rounded-full flex items-center justify-center mx-auto mb-4"> {/* Keep warm gradient */}
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Smart Organization
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Intelligent categorization, folders, templates, and search to keep everything organized.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <section id="testimonials" className="bg-white dark:bg-dark-bg py-24 relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-modular-canvas-fog dark:bg-dark-surface border border-modular-stone-alloy dark:border-dark-border 
              rounded-full px-4 py-2 mb-6">
              <span className="text-modular-frosted-indigo dark:text-modular-frosted-indigo text-sm font-medium">Testimonials</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-br from-modular-shadow-quartz via-modular-graphite-smoke to-modular-frosted-indigo bg-clip-text text-transparent"> {/* Keep this gradient */}
                What creators are saying
              </span> {/* Keep this span */}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Discover how PromptOS is transforming creative workflows for professionals worldwide.
            </p>
          </div>

          <div className="flex justify-center gap-6 mt-10 
            [mask-image:linear-gradient(to_bottom,transparent,black_25%,black_75%,transparent)] 
            max-h-[740px] overflow-hidden">
            <TestimonialsColumn testimonials={firstColumn} duration={15} />
            <TestimonialsColumn testimonials={secondColumn} className="hidden md:block" duration={19} />
            <TestimonialsColumn testimonials={thirdColumn} className="hidden lg:block" duration={17} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <div className="bg-modular-primary-gradient py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Unlock Your Creative Potential?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"> {/* Keep this p */}
            Join thousands of creators who trust PromptOS to organize and supercharge their AI workflow.
          </p>
          <Link
            to="/auth"
            className="inline-flex items-center bg-white text-modular-frosted-indigo hover:bg-gray-100 
              shadow-lg px-8 py-3 text-lg font-semibold rounded-lg transition-all duration-300"> {/* Keep this button style */}
            <Lock className="h-5 w-5 mr-2" />
            Get Started Free
          </Link>
        </div>
      </div>
    </div>
  );
}